import { Injectable } from '@angular/core';
import { BehaviorSubject, filter, Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { TokenStorageService } from './token-storage.service';
import { map } from 'rxjs/operators';
import { Backend } from '../_models/backend';
import { ApiConstants } from '../_models/api-constants';
import LoginRequest = Backend.LoginRequest;
import PairingRequest = Backend.PairingRequest;
import StudentDTOOnNotebook = Backend.StudentDTOOnNotebook;
import ClassIdSlimDTO = Backend.ClassIdSlimDTO;
import LoginInfantRequest = Backend.LoginInfantRequest;
import JwtResponse = Backend.JwtResponse;
import Role = Backend.Role;

const AUTH_ENTRY_POINT = ApiConstants.API_AUTH_ADDRESS_BASE + ApiConstants.API_AUTH_ADDRESS_REQUEST_TOKEN_STUDENT;
const AUTH_PAIR_DEVICE = ApiConstants.API_AUTH_ADDRESS_BASE + ApiConstants.API_AUTH_ADDRESS_REQUEST_TOKEN_PAIRING;
const AUTH_GUEST_LOGIN = ApiConstants.API_AUTH_ADDRESS_BASE + ApiConstants.API_AUTH_ADDRESS_REQUEST_TOKEN_GUEST_STUDENT;
const AUTH_INFANT_LOGIN_CLASS_LIST = ApiConstants.API_AUTH_ADDRESS_BASE + ApiConstants.API_PAIRING_ADDRESS_CLASSES_LIST;
const AUTH_INFANT_LOGIN_USER_LIST = ApiConstants.API_AUTH_ADDRESS_BASE + ApiConstants.API_PAIRING_ADDRESS_CLASSES_STUDENTS;
const AUTH_INFANT_STUDENT = ApiConstants.API_AUTH_ADDRESS_BASE + ApiConstants.API_AUTH_ADDRESS_REQUEST_TOKEN_INFANT_STUDENT;

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private currentUserSubject: BehaviorSubject<JwtResponse | null> = new BehaviorSubject<JwtResponse | null>(null);
  public currentUser: Observable<JwtResponse | null> = new Observable<JwtResponse | null>();

  constructor(private http: HttpClient, private tokenStorage: TokenStorageService) {
    const user = this.tokenStorage.getUser();

    if (user != null) {
      this.currentUserSubject = new BehaviorSubject<JwtResponse | null>(user);
    }
    this.currentUser = this.currentUserSubject.asObservable();
  }

  public get currentUserValue(): JwtResponse | null {
    return this.currentUserSubject.value;
  }

  login(loginRequest: LoginRequest): Observable<JwtResponse> {
    return this.http.post<JwtResponse>(AUTH_ENTRY_POINT, loginRequest)
      .pipe(map(user => {
        // store user details and jwt accessToken in session storage to keep user logged in between page refreshes
        this.tokenStorage.setUser(user);

        this.currentUserSubject.next(user);

        this.tokenStorage.saveOauthLogin(loginRequest.oauth);

        return user;
      }));
  }

  loginAsGuest(loginRequest: LoginRequest): Observable<JwtResponse> {
    return this.http.post<JwtResponse>(AUTH_GUEST_LOGIN, loginRequest)
      .pipe(map(user => {
        // store user details and jwt accessToken in session storage to keep user logged in between page refreshes
        this.tokenStorage.setUser(user);

        this.currentUserSubject.next(user);

        this.tokenStorage.saveGuestLogin(true);

        return user;
      }));
  }

  logout(full: boolean = false): void {
    if (full) {
      this.tokenStorage.clearStorage();
      this.currentUser = new Observable<JwtResponse | null>();
    } else {
      // remove user from session storage to log user out
      this.tokenStorage.clearUser();
      this.tokenStorage.clearGuestLogin();

      let pairedLoginToken = this.tokenStorage.getPairedLoginToken();
      if (pairedLoginToken != null) {
        this.setPairingUser(pairedLoginToken);
      } else {
        this.currentUser = new Observable<JwtResponse | null>();
      }
    }
    location.reload();
  }

  isOathLogin(): boolean {
    return this.tokenStorage.getOauthLogin();
  }

  isGuestLogin(): boolean {
    return this.tokenStorage.getGuestLogin();
  }

  isPairedLogin(): boolean {
    const pairedLoginToken = this.tokenStorage.getPairedLoginToken();
    return pairedLoginToken != null;
  }

  checkUser(roles: string[] | undefined ): boolean {
    const currentUser = this.currentUserValue;
    const intersect = currentUser?.roles?.filter(x => roles?.includes(x));

    return intersect !== null && intersect !== undefined && intersect.length > 0;
  }

  getUserName(): string {
    return this.currentUserValue != null && this.currentUserValue.name != null ? this.currentUserValue.name : '';
  }

  pairDevice(pairingCode: string): Observable<JwtResponse> {
    let pairingRequest: PairingRequest = { pairingCode };
    return this.http.post<JwtResponse>(AUTH_PAIR_DEVICE, pairingRequest)
      .pipe(filter(user => {
        // store pairedLoginToken in locale storage to keep it between page refreshes
        if (user.accessToken != null) {
          this.tokenStorage.setPairedLoginToken(user.accessToken);

          this.setPairingUser(user.accessToken);

          console.log(user.accessToken);
        }
        return true;
      }));
  }

  setPairingUser(pairedLoginToken: string) {
    let pairedUser: JwtResponse = { id: null, username: '', name: null, roles: ['ROLE_PAIRED_DEVICE'], accessToken: pairedLoginToken};
    this.tokenStorage.setUser(pairedUser);

    this.currentUserSubject.next(pairedUser);
  }

  listClassIds(): Observable<ClassIdSlimDTO[]> {
    return this.http.get<ClassIdSlimDTO[]>(AUTH_INFANT_LOGIN_CLASS_LIST);
  }

  listStudentsByClassId(classIdId: number): Observable<StudentDTOOnNotebook[]> {
    const url = this.replaceClassIdId(AUTH_INFANT_LOGIN_USER_LIST, classIdId);
    return this.http.get<StudentDTOOnNotebook[]>(url);
  }

  private replaceClassIdId(url: string, classIdId: number): string {
    return url.replace('{classIdId}', classIdId.toString());
  }

  loginInfant(studentId: number): Observable<JwtResponse> {
    let loginRequest: LoginInfantRequest = { studentId };
    return this.http.post<JwtResponse>(AUTH_INFANT_STUDENT, loginRequest)
      .pipe(map(user => {
        // store user details and jwt accessToken in session storage to keep user logged in between page refreshes
        this.tokenStorage.setUser(user);

        this.currentUserSubject.next(user);

        return user;
      }));
  }


  isStudent(): boolean {
    return this.hasRole('ROLE_STUDENT');
  }

  hasRole(role: Role): boolean {
    const currentUser = this.currentUserSubject.value;
    if (currentUser != null && currentUser.roles != null) {
      return currentUser.roles.indexOf(role) >= 0;
    }

    return false;
  }
}
